import { useState } from "react";
import type { TelemetryValue } from "../types/telemetry";
import {
  computeFuelProjection,
  estimateMinutesRemaining,
  nextFuelBaseline,
  type FuelBaseline,
} from "../lib/fuelProjection";

export const FUEL_LEVEL_METRIC = "fuel/fuel_level";

export interface FuelProjectionState {
  fuelLevel: number | null;
  lapsRemaining: number | null;
  basisLaps: number | null;
  minutesRemaining: number | null;
}

export function useFuelProjection(
  metrics: Map<string, Pick<TelemetryValue, "value">>,
  lapCount: number | null,
  recentLapTimesMs: readonly number[] = [],
): FuelProjectionState {
  const [baseline, setBaseline] = useState<FuelBaseline | null>(null);
  const raw = metrics.get(FUEL_LEVEL_METRIC)?.value;
  const fuelLevel = typeof raw === "number" && !isNaN(raw) ? raw : null;

  // Render-phase state adjustment (React's "adjusting state when props
  // change" pattern) — avoids a cascading setState-in-effect.
  const adjustedBaseline =
    fuelLevel !== null && lapCount !== null ? nextFuelBaseline(baseline, lapCount, fuelLevel) : baseline;
  if (adjustedBaseline !== baseline) {
    setBaseline(adjustedBaseline);
  }

  const projection = lapCount === null ? null : computeFuelProjection(adjustedBaseline, lapCount, fuelLevel);
  const lapsRemaining = projection?.lapsRemaining ?? null;

  return {
    fuelLevel,
    lapsRemaining,
    basisLaps: projection?.basisLaps ?? null,
    minutesRemaining: estimateMinutesRemaining(lapsRemaining, recentLapTimesMs),
  };
}
